import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Row, Col, Button, Spinner, Alert } from "reactstrap";
import { useSelector } from "react-redux";
import { uploadCkycDocuments, verifyCkycNumber, fetchProposalFormData } from "../OrderService";
import { extractErrorMessage, isFlowDisabledError, resolveEnabledKycFlows } from "../OrderUtils";
import { KYC_FLOW_UPLOAD_DOC, KYC_FLOW_VERIFY, KYC_FLOW_META as FLOW_META, CKYC_PROPOSAL_KEYS } from "../OrderConstants";
import { useCkycFlows } from "../Hook/useCkycFlows";
import { showToast } from "../../../../common/Toaster/Toaster";

// Without both IDs the request would go out as member_id/order_id: null
// (Number(undefined) is NaN, which JSON sends as null) and fail with a vague error.
const MISSING_IDS_MSG = "Customer or order details are missing. Please refresh the page and try again.";

// Orders/proposals store "Male"/"Female"; the verify API takes "M"/"F".
// Anything unrecognised gives "" so no gender is sent, rather than a guessed one.
const toGenderCode = (value) => {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "m" || v === "male") return "M";
  if (v === "f" || v === "female") return "F";
  return "";
};

const KycModal = ({ isOpen, toggle, orderId, memberId, detailsData, onSuccess }) => {
  // GET /ckyc/flows config, which decides the options on offer. Read before any
  // option is rendered, so an option can never flash on screen and then disappear.
  const {
    data: flowConfig,
    isFetching: flowsLoading,
    isError: flowsFailed,
    error: flowsQueryError,
    refetch: loadFlows,
  } = useCkycFlows({ enabled: !!isOpen });
  const flowsError = flowsFailed
    ? extractErrorMessage(flowsQueryError) || "Could not load the available KYC options."
    : "";

  const flows = resolveEnabledKycFlows(flowConfig);
  const singleFlow = flows.length === 1;
  const entryView = singleFlow ? FLOW_META[flows[0]].view : "chooser";
  const uploadAllowed = flows.includes(KYC_FLOW_UPLOAD_DOC);
  const verifyAllowed = flows.includes(KYC_FLOW_VERIFY);
  // Both flags off: nothing to offer and nothing to submit.
  const kycUnavailable = !flowsLoading && !flowsError && flows.length === 0;
  // Only render an option once a clean config says there is one.
  const showOptions = !flowsLoading && !flowsError && flows.length > 0;

  const [view, setView] = useState(""); // "chooser" | "doc-chooser" | "upload" | "verify"
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Flow 1 states
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [fetchingFiles, setFetchingFiles] = useState(false);
  const [uploadDocType, setUploadDocType] = useState(""); // "AADHAAR" | "PAN"

  // Flow 2 states
  const customerType = "I"; // Individual only
  const [idType, setIdType] = useState("PAN"); // "PAN" | "AADHAAR"
  const [idNum, setIdNum] = useState("");
  const [dob, setDob] = useState("");
  const [fullName, setFullName] = useState("");
  const [gender, setGender] = useState(""); // "M" | "F" | "" (not sent)
  const [mobile, setMobile] = useState("");
  const [proposalPanNum, setProposalPanNum] = useState("");
  const [proposalAadhaarNum, setProposalAadhaarNum] = useState("");

  const selectedUser = useSelector((state) => state.header.selectedUser);
  const organizationId = selectedUser?.organizationid || detailsData?.organizationid || detailsData?.ag_org_id || detailsData?.organization_id;

  // Open on whatever is enabled once the config lands, and follow it if the
  // flags change later.
  const flowsKey = flows.join("|");
  const flowsKeyRef = useRef("");
  useEffect(() => {
    if (flowsLoading || flowsError || !flows.length) return;
    if (flowsKeyRef.current === flowsKey) return;
    flowsKeyRef.current = flowsKey;
    setView(entryView);
  }, [flowsLoading, flowsError, flows.length, flowsKey, entryView]);

  // Pre-fill runs once per open. detailsData changes again when the order is
  // re-read after a failed attempt, and re-running then would wipe the user's
  // corrections.
  const prefilledRef = useRef(false);
  // Set once the user edits the name / ID number / DOB, so the slower proposal
  // fetch does not overwrite what they typed.
  const nameTouchedRef = useRef(false);
  const idNumTouchedRef = useRef(false);
  const dobTouchedRef = useRef(false);

  useEffect(() => {
    if (detailsData && !prefilledRef.current) {
      prefilledRef.current = true;
      const fName = detailsData.billing_first_name || "";
      const lName = detailsData.billing_last_name || "";
      setFullName(`${fName} ${lName}`.trim());
      setMobile(detailsData.billing_contact || "");
      setDob(detailsData.billing_dob || detailsData.dob || "");
      setGender(toGenderCode(detailsData.billing_gender || detailsData.gender));
      const panVal = detailsData.pan_number || detailsData.billing_pan || detailsData.pan || "";
      if (panVal) {
        setProposalPanNum(panVal);
        setIdNum(panVal);
      }
    }
  }, [detailsData]);

  // Helper to fetch file from URL and construct a File object
  const fetchFileFromUrl = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Fetch failed");
      const blob = await response.blob();
      const mimeType = blob.type || "image/jpeg";
      return new File([blob], filename, { type: mimeType });
    } catch (e) {
      console.error("Error fetching file:", e);
      return null;
    }
  };

  useEffect(() => {
    if (isOpen && orderId && organizationId) {
      const loadProposalFiles = async () => {
        setFetchingFiles(true);
        try {
          const proposalData = await fetchProposalFormData(orderId, "VIEW", organizationId);
          if (proposalData) {
            // Read the exact proposal keys only (see CKYC_PROPOSAL_KEYS)
            const rawName = proposalData[CKYC_PROPOSAL_KEYS.name];
            const fetchedName = typeof rawName === "string" ? rawName.trim() : "";
            const rawPan = proposalData[CKYC_PROPOSAL_KEYS.pan];
            const fetchedPan = typeof rawPan === "string" ? rawPan.trim() : "";
            const rawDob = proposalData[CKYC_PROPOSAL_KEYS.dob];
            const fetchedDob = typeof rawDob === "string" ? rawDob.trim() : "";

            // Proposal name wins over the order's billing name; billing stays as the fallback
            if (fetchedName && !nameTouchedRef.current) {
              setFullName(fetchedName);
            }
            // Same for gender (not editable in the form, so no touched check)
            const fetchedGender = toGenderCode(proposalData[CKYC_PROPOSAL_KEYS.gender]);
            if (fetchedGender) {
              setGender(fetchedGender);
            }
            if (fetchedPan) {
              setProposalPanNum(fetchedPan);
              // PAN is the default idType; only fill if the user hasn't edited or switched type
              if (!idNumTouchedRef.current) setIdNum(fetchedPan);
            }
            if (fetchedDob && !dobTouchedRef.current) {
              // Convert date format if needed: dd-MM-yyyy -> yyyy-MM-dd
              if (fetchedDob.includes("-")) {
                const parts = fetchedDob.split("-");
                if (parts[0].length === 2 && parts[2].length === 4) {
                  setDob(`${parts[2]}-${parts[1]}-${parts[0]}`);
                } else {
                  setDob(fetchedDob);
                }
              } else {
                setDob(fetchedDob);
              }
            }

            const panFileObj = proposalData[CKYC_PROPOSAL_KEYS.panFile];
            const aadhaarFileObj = proposalData[CKYC_PROPOSAL_KEYS.aadhaarFile];

            if (panFileObj && panFileObj.media_url) {
              const file = await fetchFileFromUrl(
                panFileObj.media_url,
                panFileObj.file_name || "pan.jpg"
              );
              if (file) setPanFile(file);
            }
            if (aadhaarFileObj && aadhaarFileObj.media_url) {
              const file = await fetchFileFromUrl(
                aadhaarFileObj.media_url,
                aadhaarFileObj.file_name || "aadhaar.jpg"
              );
              if (file) setAadhaarFile(file);
            }
          }
        } catch (err) {
          console.error("Failed to load proposal files", err);
        } finally {
          setFetchingFiles(false);
        }
      };
      loadProposalFiles();
    }
  }, [isOpen, orderId, organizationId]);

  const handleClose = () => {
    if (loading) return; // Prevent closing while processing slow request
    setErrorMsg("");
    setView(entryView);
    setAadhaarFile(null);
    setPanFile(null);
    setUploadDocType("");
    setIdNum("");
    setDob("");
    setProposalPanNum("");
    setProposalAadhaarNum("");
    prefilledRef.current = false;
    nameTouchedRef.current = false;
    idNumTouchedRef.current = false;
    dobTouchedRef.current = false;
    toggle();
  };

  const handleFileChange = (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        showToast("Invalid file format. Please upload JPG, PNG, or PDF.", "error");
        e.target.value = null;
        return;
      }
      setter(file);
    }
  };

  // Re-read the order before the step re-renders, so the options reflect the
  // state the backend is in *after* this attempt rather than before it.
  const refreshOrder = async (data) => {
    if (!onSuccess) return;
    try {
      await onSuccess(data);
    } catch (err) {
      console.error("Failed to re-read order after CKYC attempt", err);
    }
  };

  // Flow 1 Submit
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!memberId || !orderId) {
      setErrorMsg(MISSING_IDS_MSG);
      return;
    }
    if (uploadDocType === "AADHAAR" && !aadhaarFile) {
      setErrorMsg("Please upload your Aadhaar document.");
      return;
    }
    if (uploadDocType === "PAN" && !panFile) {
      setErrorMsg("Please upload your PAN document.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    try {
      const response = await uploadCkycDocuments(memberId, orderId, {
        aadhaar: uploadDocType === "AADHAAR" ? aadhaarFile : undefined,
        pan: uploadDocType === "PAN" ? panFile : undefined,
      });

      const resData = response?.data;
      const fStatus = resData?.final_status !== undefined && resData?.final_status !== null
        ? String(resData.final_status)
        : resData?.member_ckyc?.final_status !== undefined && resData?.member_ckyc?.final_status !== null
          ? String(resData.member_ckyc.final_status)
          : "";
      const kStatus = resData?.kyc_status || resData?.member_ckyc?.kyc_status || "";

      if (fStatus === "1" || fStatus === "3" || kStatus === "Completed") {
        showToast("Verification successful!", "success");
      } else {
        showToast("Document uploaded and submitted successfully!", "success");
      }

      await refreshOrder(response.data);
      handleClose();
    } catch (error) {
      // Message only: the axios error carries the request body (base64 ID documents).
      console.error("CKYC document upload failed:", error?.message);

      // The flag was switched off between loading the options and submitting:
      // show the backend's message, then re-read the config and re-render.
      if (isFlowDisabledError(error)) {
        const disabledMsg = extractErrorMessage(error);
        setErrorMsg(disabledMsg);
        showToast(disabledMsg, "error");
        await loadFlows();
        return;
      }

      const msg = error.response?.data?.error_message || "Document upload failed. Please try again.";
      setErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  // The in-progress markers only mean something when the customer is sent to the
  // insurer and comes back. Left behind otherwise, the order page reads them as a
  // return from the insurer and toasts the outcome a second time.
  const clearCkycInProgress = () => {
    ["ckyc_in_progress", "ckyc_member_id", "ckyc_order_id"].forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  };

  // Flow 2 Submit
  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (!memberId || !orderId) {
      setErrorMsg(MISSING_IDS_MSG);
      return;
    }
    if (!idNum.trim()) {
      setErrorMsg("Document ID number is required.");
      return;
    }
    if (!dob) {
      setErrorMsg("Date of Birth is required.");
      return;
    }
    if (!fullName.trim()) {
      setErrorMsg("Full Name is required.");
      return;
    }

    // Format DOB to dd-MM-yyyy safely
    let formattedDob = dob;
    if (dob && dob.includes("-")) {
      const parts = dob.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM-DD -> DD-MM-YYYY
        formattedDob = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else if (parts[0].length === 2 && parts[2].length === 4) {
        // Already DD-MM-YYYY
        formattedDob = dob;
      }
    }

    sessionStorage.setItem("ckyc_in_progress", "true");
    sessionStorage.setItem("ckyc_member_id", String(memberId || ""));
    sessionStorage.setItem("ckyc_order_id", String(orderId || ""));
    localStorage.setItem("ckyc_in_progress", "true");
    localStorage.setItem("ckyc_member_id", String(memberId || ""));
    localStorage.setItem("ckyc_order_id", String(orderId || ""));

    setLoading(true);
    setErrorMsg("");

    const payload = {
      member_id: Number(memberId),
      order_id: Number(orderId),
      customer_type: customerType,
      id_type: idType,
      id_num: idNum.trim().toUpperCase(),
      dob: formattedDob,
      full_name: fullName.trim(),
      gender: gender || undefined,
      mobile: mobile.trim() || undefined
    };

    try {
      const response = await verifyCkycNumber(payload);
      const resData = response?.data;

      // Extract redirect_url across all response shapes (direct, nested member_ckyc, provider_response, or last_response)
      let redirectUrl =
        resData?.redirect_url ||
        resData?.member_ckyc?.redirect_url ||
        resData?.provider_response?.response?.redirect_url ||
        resData?.provider_response?.response?.redirectUrl ||
        resData?.provider_response?.redirect_url;

      if (!redirectUrl && resData?.member_ckyc?.last_response) {
        try {
          const parsed = typeof resData.member_ckyc.last_response === "string" 
            ? JSON.parse(resData.member_ckyc.last_response) 
            : resData.member_ckyc.last_response;
          redirectUrl = parsed?.redirect_url || parsed?.redirectUrl || parsed?.response?.redirect_url || parsed?.response?.redirectUrl;
        } catch (e) {
          console.error("Error parsing member_ckyc.last_response for redirect_url:", e);
        }
      }

      if (!redirectUrl && resData?.last_response) {
        try {
          const parsed = typeof resData.last_response === "string" 
            ? JSON.parse(resData.last_response) 
            : resData.last_response;
          redirectUrl = parsed?.redirect_url || parsed?.redirectUrl || parsed?.response?.redirect_url || parsed?.response?.redirectUrl;
        } catch (e) {
          console.error("Error parsing last_response for redirect_url:", e);
        }
      }

      const proposalId =
        resData?.member_ckyc?.proposal_id ||
        resData?.provider_response?.response?.proposalId ||
        resData?.proposal_id ||
        resData?.member_ckyc?.req_id ||
        resData?.req_id;

      const ckycRef =
        resData?.member_ckyc?.ckyc_ref ||
        resData?.ckyc_ref ||
        resData?.member_ckyc?.code ||
        resData?.code;

      const apiBase = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
      const insurerReturnUrl =
        resData?.insurer_return_url ||
        resData?.member_ckyc?.insurer_return_url ||
        (ckycRef ? `${apiBase}/ckyc/return/${ckycRef}` : null);

      if (redirectUrl) {
        if (!insurerReturnUrl) {
          const err = "Missing CKYC reference for return redirection. Please try again.";
          clearCkycInProgress();
          setErrorMsg(err);
          showToast(err, "error");
          return;
        }

        showToast("Redirecting you to the verification portal...", "success");

        // Dynamically create and auto-submit the POST form required by Future Generali
        const form = document.createElement("form");
        form.method = "POST";
        form.action = redirectUrl;
        form.id = "KYCFailedFormTag";
        form.name = "redirect";

        const addHiddenField = (name, value) => {
          if (value !== undefined && value !== null && value !== "") {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.id = name;
            input.value = value;
            form.appendChild(input);
          }
        };

        if (proposalId) {
          addHiddenField("VISoF_KYC_Req_No", proposalId);
          addHiddenField("IC_KYC_No", proposalId);
        }
        addHiddenField("VISoF_Return_URL", insurerReturnUrl);

        document.body.appendChild(form);
        form.submit();
        return;
      }

      // no redirect - the outcome is final here and this modal reports it
      clearCkycInProgress();

      const fStatus = resData?.final_status !== undefined && resData?.final_status !== null
        ? String(resData.final_status)
        : resData?.member_ckyc?.final_status !== undefined && resData?.member_ckyc?.final_status !== null
          ? String(resData.member_ckyc.final_status)
          : "";
      const kStatus = resData?.kyc_status || resData?.member_ckyc?.kyc_status || "";

      if (fStatus === "1" || fStatus === "3" || kStatus === "Completed") {
        showToast("Verification successful!", "success");
        await refreshOrder(response.data);
        handleClose();
      } else if (fStatus === "0" || kStatus === "Failed") {
        const failMsg = resData?.ckyc_remarks || resData?.member_ckyc?.ckyc_remarks || resData?.provider_response?.response?.message || "Verification failed. Please check your PAN details or try with Aadhaar.";
        setErrorMsg(failMsg);
        showToast(failMsg, "error");
        await refreshOrder(response.data);
      } else {
        showToast("Verification initiated successfully!", "success");
        await refreshOrder(response.data);
        handleClose();
      }
    } catch (error) {
      // Message only: the axios error carries the request body (ID number, DOB, name).
      console.error("CKYC verify request failed:", error?.message);
      clearCkycInProgress();

      if (isFlowDisabledError(error)) {
        const disabledMsg = extractErrorMessage(error);
        setErrorMsg(disabledMsg);
        showToast(disabledMsg, "error");
        await loadFlows();
        return;
      }

      const msg = error.response?.data?.error_message || error.response?.data?.ckyc_remarks || "Verification request failed.";
      setErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalHeader toggle={handleClose} className="border-0 pb-0 text-dark">
        <span className="fw-semibold fs-5">
          {(!showOptions || view === "chooser") && "Central KYC (CKYC) Verification"}
          {showOptions && view === "doc-chooser" && "Select Document to Upload"}
          {showOptions && view === "upload" && `Upload ${uploadDocType === "AADHAAR" ? "Aadhaar Card" : "PAN Card"}`}
          {showOptions && view === "verify" && "Verify with ID Details"}
        </span>
      </ModalHeader>
      <ModalBody className="p-4">
        {errorMsg && <Alert color="danger" className="rounded-3">{errorMsg}</Alert>}

        {flowsLoading && (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 mb-0 text-muted">Checking available KYC options...</p>
          </div>
        )}

        {!flowsLoading && flowsError && (
          <div className="text-center py-4">
            <span className="d-block fs-1 lh-1 mb-3">!</span>
            <h6 className="fw-semibold text-dark mb-2">Could not load KYC options</h6>
            <p className="text-muted mb-4">{flowsError}</p>
            <div className="d-flex justify-content-center gap-2">
              <Button type="button" color="light" onClick={handleClose}>
                Close
              </Button>
              <Button type="button" color="primary" onClick={() => loadFlows()}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {kycUnavailable && (
          <div className="text-center py-4">
            <span className="d-block fs-1 lh-1 mb-3">!</span>
            <p className="text-muted mb-4">
              KYC verification is currently unavailable, please contact support
            </p>
            <Button type="button" color="light" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}

        {showOptions && view === "chooser" && (
          <div>
            <p className="text-muted mb-4">
              Complete your KYC to finalize your order. Choose one of the options below:
            </p>
            <div className="d-flex flex-column gap-3">
              {flows.map((flow) => (
                <div
                  key={flow}
                  className="p-4 border rounded-3 cursor-pointer hover-card shadow-sm-hover transition-all d-flex align-items-center justify-content-between"
                  onClick={() => setView(FLOW_META[flow].view)}
                  style={{ cursor: "pointer", border: "1px solid #e9ebec" }}
                >
                  <div>
                    <h6 className="mb-1 text-primary fw-semibold">{FLOW_META[flow].title}</h6>
                    <small className="text-muted">{FLOW_META[flow].subtitle}</small>
                  </div>
                  <i className="ri-arrow-right-s-line fs-3 text-muted"></i>
                </div>
              ))}
            </div>

          </div>
        )}

        {showOptions && uploadAllowed && view === "doc-chooser" && (
          <div>
            <p className="text-muted mb-4">
              Select the document you wish to upload for verification:
            </p>
            <div className="d-flex flex-column gap-3">
              {/* Aadhaar Card Select */}
              <div
                className="p-4 border rounded-3 cursor-pointer hover-card shadow-sm-hover transition-all d-flex align-items-center justify-content-between"
                onClick={() => {
                  setUploadDocType("AADHAAR");
                  setView("upload");
                }}
                style={{ cursor: "pointer", border: "1px solid #e9ebec" }}
              >
                <div>
                  <h6 className="mb-1 text-primary fw-semibold">Aadhaar Card</h6>
                  <small className="text-muted">Upload your Aadhaar Card (JPG, PNG, PDF)</small>
                </div>
                <i className="ri-arrow-right-s-line fs-3 text-muted"></i>
              </div>

              {/* PAN Card Select */}
              <div
                className="p-4 border rounded-3 cursor-pointer hover-card shadow-sm-hover transition-all d-flex align-items-center justify-content-between"
                onClick={() => {
                  setUploadDocType("PAN");
                  setView("upload");
                }}
                style={{ cursor: "pointer", border: "1px solid #e9ebec" }}
              >
                <div>
                  <h6 className="mb-1 text-primary fw-semibold">PAN Card</h6>
                  <small className="text-muted">Upload your PAN Card (JPG, PNG, PDF)</small>
                </div>
                <i className="ri-arrow-right-s-line fs-3 text-muted"></i>
              </div>
            </div>

            {!singleFlow && (
              <div className="d-flex justify-content-end mt-4">
                <Button type="button" color="light" onClick={() => setView("chooser")} disabled={loading}>
                  Back
                </Button>
              </div>
            )}
          </div>
        )}

        {showOptions && uploadAllowed && view === "upload" && (
          <Form onSubmit={handleUploadSubmit}>
            {fetchingFiles && (
              <div className="mb-3 text-center p-3 bg-light border rounded-3 animate__animated animate__fadeIn">
                <Spinner size="sm" color="primary" className="me-2" />
                <span className="small text-muted fw-semibold">Pre-loading document uploads from proposal form...</span>
              </div>
            )}
            <Row className="gy-3">
              {uploadDocType === "AADHAAR" && (
                <Col md={12}>
                  <FormGroup>
                    <Label className="fw-medium">Aadhaar File</Label>
                    {aadhaarFile ? (
                      <div className="d-flex align-items-center justify-content-between p-2 border rounded-3 bg-light animate__animated animate__fadeIn">
                        <span className="text-truncate fw-medium text-dark text-start flex-grow-1" style={{ maxWidth: "80%" }}>
                          📄 {aadhaarFile.name} <small className="text-muted fw-normal">(Linked from Proposal)</small>
                        </span>
                        <Button size="sm" color="danger" className="py-1 px-2 border-0 rounded-2 text-white" onClick={() => setAadhaarFile(null)}>Clear</Button>
                      </div>
                    ) : (
                      <>
                        <Input type="file" onChange={(e) => handleFileChange(e, setAadhaarFile)} accept=".jpg,.jpeg,.png,.pdf" disabled={loading || fetchingFiles} />
                        <small className="text-muted">JPEG, PNG, or PDF</small>
                      </>
                    )}
                  </FormGroup>
                </Col>
              )}
              {uploadDocType === "PAN" && (
                <Col md={12}>
                  <FormGroup>
                    <Label className="fw-medium">PAN File</Label>
                    {panFile ? (
                      <div className="d-flex align-items-center justify-content-between p-2 border rounded-3 bg-light animate__animated animate__fadeIn">
                        <span className="text-truncate fw-medium text-dark text-start flex-grow-1" style={{ maxWidth: "80%" }}>
                          📄 {panFile.name} <small className="text-muted fw-normal">(Linked from Proposal)</small>
                        </span>
                        <Button size="sm" color="danger" className="py-1 px-2 border-0 rounded-2 text-white" onClick={() => setPanFile(null)}>Clear</Button>
                      </div>
                    ) : (
                      <>
                        <Input type="file" onChange={(e) => handleFileChange(e, setPanFile)} accept=".jpg,.jpeg,.png,.pdf" disabled={loading || fetchingFiles} />
                        <small className="text-muted">JPEG, PNG, or PDF</small>
                      </>
                    )}
                  </FormGroup>
                </Col>
              )}
            </Row>

            {loading && (
              <div className="mt-4 p-3 bg-light rounded-3 text-center border">
                <Spinner size="sm" color="primary" className="me-2" />
                <span className="fw-medium text-dark">Uploading and processing documents. Do not close this modal...</span>
                <div className="progress mt-2" style={{ height: "4px" }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated w-100 bg-primary" role="progressbar"></div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button type="button" color="light" onClick={() => setView("doc-chooser")} disabled={loading}>
                Back
              </Button>
              <Button type="submit" color="primary" disabled={loading || (uploadDocType === "AADHAAR" ? !aadhaarFile : !panFile)}>
                {loading ? "Submitting..." : "Submit Documents"}
              </Button>
            </div>
          </Form>
        )}

        {showOptions && verifyAllowed && view === "verify" && (
          <Form onSubmit={handleVerifySubmit}>
            <Row className="gy-3">
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-medium">ID Type</Label>
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      color={idType === "PAN" ? "primary" : "outline-primary"}
                      onClick={() => { idNumTouchedRef.current = true; setIdType("PAN"); setIdNum(proposalPanNum); }}
                      className="flex-fill"
                      disabled={loading}
                    >
                      PAN
                    </Button>
                    <Button
                      type="button"
                      color={idType === "AADHAAR" ? "primary" : "outline-primary"}
                      onClick={() => { idNumTouchedRef.current = true; setIdType("AADHAAR"); setIdNum(proposalAadhaarNum); }}
                      className="flex-fill"
                      disabled={loading}
                    >
                      Aadhaar
                    </Button>
                  </div>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label className="fw-medium">Date of Birth</Label>
                  <Input type="date" value={dob} onChange={(e) => { dobTouchedRef.current = true; setDob(e.target.value); }} required disabled={loading} />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label className="fw-medium">Full Name (as per ID)</Label>
                  <Input type="text" placeholder="Enter Full Name" value={fullName} onChange={(e) => { nameTouchedRef.current = true; setFullName(e.target.value); }} required disabled={loading} />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label className="fw-medium">{idType === "PAN" ? "PAN Number" : "Aadhaar Number"}</Label>
                  <Input
                    type="text"
                    placeholder={idType === "PAN" ? "Enter 10-digit PAN" : "Enter 12-digit Aadhaar"}
                    value={idNum}
                    onChange={(e) => { idNumTouchedRef.current = true; setIdNum(e.target.value); }}
                    maxLength={idType === "PAN" ? 10 : 12}
                    required
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
            </Row>

            {loading && (
              <div className="mt-4 p-3 bg-light rounded-3 text-center border">
                <Spinner size="sm" color="primary" className="me-2" />
                <span className="fw-medium text-dark">Initiating verification request...</span>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              {!singleFlow && (
                <Button type="button" color="light" onClick={() => setView("chooser")} disabled={loading}>
                  Back
                </Button>
              )}
              <Button type="submit" color="primary" disabled={loading}>
                {loading ? "Processing..." : "Verify & Redirect"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default KycModal;
